import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import Button from '@msi/cobalt-react/button';
import Icon from '@msi/cobalt-react/icon';
import ApiService from '../../services/apiService';
import { Api } from '../../types/apiData';
import { ApiComparisonModel } from '../../types/apiDiff';
import { normalizeApiDiff } from '../../utils/apiComparisonNormalizer';
import ComparisonFilterBar, { FilterKey, toFilterKey } from '../../components/CompareVersions/ComparisonFilterBar';
import ComparisonSectionView from '../../components/CompareVersions/ComparisonSectionView';
import '../../components/CompareVersions/CompareVersions.css';

const ALL_FILTERS: FilterKey[] = ['added', 'changed', 'deprecated-removed'];

/** Resolves a from/to URL identifier (version number or version document id) against the fetched version list. */
function resolveVersionNumber(identifier: string, versions: Api[]): string | undefined {
    const byVersionNumber = versions.find((v) => v.latestApiVersionNumber === identifier);
    if (byVersionNumber) return byVersionNumber.latestApiVersionNumber;
    const byId = versions.find((v) => v.id === identifier);
    return byId?.latestApiVersionNumber;
}

function findVersionDoc(identifier: string, versions: Api[]): Api | undefined {
    return versions.find((v) => v.id === identifier || v.latestApiVersionNumber === identifier);
}

const CompareVersions: React.FC = () => {
    const { apiId } = useParams<{ apiId: string }>();
    const [searchParams] = useSearchParams();
    const { authState } = useOktaAuth();
    const navigate = useNavigate();
    const apiService = useMemo(() => new ApiService(), []);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [model, setModel] = useState<ApiComparisonModel | null>(null);
    const [fromApi, setFromApi] = useState<Api | undefined>(undefined);
    const [toApi, setToApi] = useState<Api | undefined>(undefined);
    const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(new Set(ALL_FILTERS));

    // Supports both direct entry (version numbers) and notification email deep-links (version document ids).
    const fromParam = searchParams.get('from') || searchParams.get('previousVersionId') || '';
    const toParam = searchParams.get('to') || searchParams.get('newVersionId') || '';

    useEffect(() => {
        // React Router reuses this component across param changes, so guard every state
        // update against a stale run (e.g. a slower request for a previous from/to pair).
        let cancelled = false;
        const abortController = new AbortController();

        const run = async () => {
            if (!apiId || !fromParam || !toParam) {
                if (!cancelled) {
                    setError('Missing or invalid version parameters. Please select two versions to compare.');
                    setLoading(false);
                }
                return;
            }
            if (!authState) {
                return;
            }

            setLoading(true);
            setError(null);
            try {
                // Fetch the version list and attempt the diff call in parallel; the diff call
                // assumes the params are version numbers, which is the common case.
                const versionsPromise = apiService
                    .getApiVersions(apiId, authState, abortController.signal)
                    .catch(() => [] as Api[]);
                const optimisticDiffPromise = apiService
                    .compareApiVersions(apiId, fromParam, toParam, authState, abortController.signal)
                    .catch(() => null);

                const [versions, optimisticDiff] = await Promise.all([versionsPromise, optimisticDiffPromise]);

                let diffResponse = optimisticDiff;
                if (!diffResponse) {
                    // Params were likely version document ids (e.g. from a notification email) — resolve them.
                    const resolvedFrom = resolveVersionNumber(fromParam, versions);
                    const resolvedTo = resolveVersionNumber(toParam, versions);
                    if (!resolvedFrom || !resolvedTo) {
                        throw new Error('Could not resolve the requested versions for this API.');
                    }
                    diffResponse = await apiService.compareApiVersions(
                        apiId,
                        resolvedFrom,
                        resolvedTo,
                        authState,
                        abortController.signal
                    );
                }

                if (cancelled) return;
                setFromApi(findVersionDoc(fromParam, versions));
                setToApi(findVersionDoc(toParam, versions));
                setModel(normalizeApiDiff(diffResponse));
            } catch (err: any) {
                if (cancelled) return;
                console.error('Error loading version comparison:', err);
                setError(err?.response?.data?.error || err?.message || 'Unable to load this version comparison.');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        run();

        return () => {
            cancelled = true;
            abortController.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiId, fromParam, toParam, authState]);

    const toggleFilter = (key: FilterKey) => {
        setActiveFilters((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const filteredSections = useMemo(() => {
        if (!model) return [];
        return model.sections.map((section) => ({
            ...section,
            entries: section.entries.filter((entry) => activeFilters.has(toFilterKey(entry.status))),
        }));
    }, [model, activeFilters]);

    const counts = useMemo(() => {
        const base: Record<FilterKey, number> = { added: 0, changed: 0, 'deprecated-removed': 0 };
        if (!model) return base;
        model.sections.forEach((section) => {
            section.entries.forEach((entry) => {
                base[toFilterKey(entry.status)] += 1;
            });
        });
        return base;
    }, [model]);

    if (loading) {
        return <div className="container mt-4">Loading comparison...</div>;
    }

    if (error || !model) {
        return (
            <div className="container mt-4">
                <div className="compare-page compare-error-state">
                    <h4>We couldn't load this comparison</h4>
                    <p>{error || 'Unknown error.'}</p>
                    <div className="d-flex justify-content-center gap-2">
                        {apiId && (
                            <Button text="Back to API Details" onClick={() => navigate(`/api-details/${apiId}`)} />
                        )}
                        <Button text="Go to Home" buttonType="action" onClick={() => navigate('/')} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-4 compare-container">
            <div className="compare-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <button
                        type="button"
                        aria-label="Go back"
                        onClick={() => navigate(-1)}
                        className="menu-link"
                        style={{ cursor: 'pointer', border: 0, padding: 0, background: 'transparent' }}
                    >
                        <Icon name="ic_back" size={36} color="primary" className="me-2" />
                    </button>
                    <h4 className="mb-0">Comparing API Versions</h4>
                </div>
                {apiId && <Button text="Back to API Details" onClick={() => navigate(`/api-details/${apiId}`)} />}
            </div>

            <div className="compare-content">
                <div className="compare-page">
                    <div className="compare-panels">
                        <div className="compare-panel">
                            <div className="compare-panel-heading">
                                <span className="compare-version-pill from">From v{model.fromVersion}</span>
                            </div>
                            <div>{fromApi?.fullApiName || 'Version details unavailable'}</div>
                        </div>
                        <div className="compare-panel">
                            <div className="compare-panel-heading">
                                <span className="compare-version-pill to">To v{model.toVersion}</span>
                            </div>
                            <div>{toApi?.fullApiName || 'Version details unavailable'}</div>
                        </div>
                    </div>

                    <ComparisonFilterBar activeFilters={activeFilters} onToggle={toggleFilter} counts={counts} />

                    {model.totalChangeCount === 0 && (
                        <p className="text-muted">No differences were found between these versions.</p>
                    )}

                    {filteredSections.map((section) => (
                        <ComparisonSectionView key={section.key} section={section} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CompareVersions;
